import appI18n from '../../i18n';
import LocalizedMessage from '../common/LocalizedMessage';
import React from "react";
import "react-toastify/dist/ReactToastify.css";
import { connect } from "react-redux";
import * as PropTypes from "prop-types";
import { history } from "../../redux/store/configureStore";
import LoginForm from "./LoginForm";
import ContextChoice from "../auth/ContextChoice";
import SuperAdminSchoolChoice from "../auth/SuperAdminSchoolChoice";
import SsoProfileCompletion from "../auth/SsoProfileCompletion";
import { GOOGLE_CLIENT_ID, ROLES, STATUS } from "../common/Const";
import { email, mandatory, validateValues } from "../common/Validations";
import * as userActions from "../../redux/actions/usersActions";
import { hasNoErrors } from "../common/Helpers";
import {
  clearLastSelectedContextKey,
  getLastSelectedContextKey,
  rememberLastSelectedContextKey,
} from "../../services/accountContextStorage";
import {
  initializeGoogleIdentityButton,
  loadGoogleIdentityScript,
} from "./googleIdentity";
import { Translation } from 'react-i18next';

const validationConfig = {
  username: {
    label: "Email",
    validate: [mandatory, email],
  },
  password: {
    label: "Hasło",
    validate: [mandatory],
  },
};

const GOOGLE_LOGIN_FAILURE_MESSAGE = "Nie udało się zalogować przez Google. Spróbuj ponownie.";

const getContextChoiceAccountId = (contextChoice, fallbackEmail) => (
  contextChoice.accountId || contextChoice.email || fallbackEmail
);

const findContextByKey = (contexts = [], contextKey) => (
  contexts.find(context => context.contextKey === contextKey) || null
);

const handleCancel = () => {
  history.goBack();
};

export class Login extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      credentials: { username: "", password: "" },
      errors: {},
      touched: {},
      status: STATUS.IDLE,
      contextChoice: null,
      schoolChoice: null,
      ssoProfileCompletion: null,
    };
    this.isCancelled = false;
    this.googleAuthRequested = false;
    this.googleButtonRef = React.createRef();
  }

  componentDidMount() {
    appI18n.on('languageChanged', this.refreshGoogleButton);
    this.refreshGoogleButton();
  }

  refreshGoogleButton = () => {
    loadGoogleIdentityScript(undefined, undefined, appI18n.resolvedLanguage)
      .then(() => {
        if (this.isCancelled || !this.googleButtonRef.current) {
          return;
        }

        initializeGoogleIdentityButton({
          clientId: GOOGLE_CLIENT_ID,
          language: appI18n.resolvedLanguage,
          buttonElement: this.googleButtonRef.current,
          onClick: this.handleGoogleAttempt,
          onCredential: this.handleGoogleCredential,
        });
      })
      .catch(() => undefined);
  }

  componentWillUnmount() {
    appI18n.off('languageChanged', this.refreshGoogleButton);
    this.isCancelled = true;
  }

  handleBlur = (event) => {
    const { name } = event.target;
    const { touched } = this.state;
    this.setState({ touched: { ...touched, [name]: true } });
    this.validate();
  };

  handleChange = (event) => {
    const { credentials } = this.state;
    const { name, value } = event.target;
    credentials[name] = value;
    this.setState({ credentials });
    this.validate();
  };

  handleSubmit = (event) => {
    if (event) event.preventDefault();
    this.setState({ status: STATUS.SUBMITTING });
    const { credentials } = this.state;
    const { signIn } = this.props;
    const isValid = this.validate();

    if (isValid) {
      signIn({ id: credentials.username, password: credentials.password })
        .then((result) => {
          if (!this.isCancelled) {
            this.handleAuthResult(result);
          }
        })
        .catch((error) => {
          if (!this.isCancelled) {
            this.setState({ errors: { saveError: error.message }, status: STATUS.SUBMITTED });
          }
        });
    } else {
      this.setState({ status: STATUS.SUBMITTED });
    }
  };

  handleGoogleAttempt = () => {
    this.googleAuthRequested = true;
  };

  handleGoogleCredential = (response) => {
    const { signInWithGoogle } = this.props;

    if (!response || !response.credential) {
      return this.handleGoogleFailure();
    }

    this.googleAuthRequested = true;
    this.setState({ status: STATUS.SUBMITTING });

    return signInWithGoogle(response.credential)
      .then((result) => {
        if (!this.isCancelled) {
          this.handleAuthResult(result);
          this.googleAuthRequested = false;
        }
      })
      .catch((error) => {
        if (!this.isCancelled) {
          this.setState({
            errors: { saveError: error.message },
            status: STATUS.SUBMITTED,
          });
        }
      });
  };

  handleAuthResult = (result) => {
    if (result && result.status === "context_choice_required") {
      const accountId = getContextChoiceAccountId(result, this.state.credentials.username);
      const preferredContextKey = getLastSelectedContextKey(accountId);
      const preferredContext = findContextByKey(result.availableContexts, preferredContextKey);

      if (preferredContext) {
        return this.handleContextSelect(preferredContext, result, accountId);
      }

      this.setState({
        status: STATUS.IDLE,
        errors: {},
        contextChoice: { ...result, accountId },
        schoolChoice: null,
        ssoProfileCompletion: null,
      });
      return null;
    }

    if (result && result.status === "school_choice_required") {
      this.setState({
        status: STATUS.IDLE,
        errors: {},
        contextChoice: null,
        schoolChoice: result,
        ssoProfileCompletion: null,
      });
      return null;
    }

    if (result && result.status === "profile_completion_required") {
      this.setState({
        status: STATUS.IDLE,
        errors: {},
        contextChoice: null,
        schoolChoice: null,
        ssoProfileCompletion: result,
      });
      return null;
    }

    this.setState({
      status: STATUS.COMPLETED,
      errors: {},
      contextChoice: null,
      schoolChoice: null,
      ssoProfileCompletion: null,
    });

    if (result && result.role === ROLES.CLIENT && history.location.pathname !== "/client" && !history.location.pathname.startsWith("/client/support") && !history.location.pathname.startsWith("/support/")) {
      history.push("/client");
    }

    return null;
  };

  handleContextSelect = (context, contextChoiceOverride, accountIdOverride) => {
    const { selectLoginContext } = this.props;
    const contextChoice = contextChoiceOverride || this.state.contextChoice;
    const accountId = accountIdOverride || getContextChoiceAccountId(contextChoice, this.state.credentials.username);
    const contextChoiceState = contextChoice ? { ...contextChoice, accountId } : null;

    this.setState({ status: STATUS.SUBMITTING });
    return selectLoginContext(contextChoice.contextChoiceToken, context.contextKey)
      .then((result) => {
        rememberLastSelectedContextKey(accountId, context.contextKey);
        return this.handleAuthResult(result);
      })
      .catch((error) => {
        clearLastSelectedContextKey(accountId);
        this.setState({
          errors: { saveError: error.message },
          status: STATUS.SUBMITTED,
          contextChoice: contextChoiceState,
          ssoProfileCompletion: null,
        });
      });
  };

  handleSchoolSelect = (school) => {
    const { selectSuperAdminSchool } = this.props;
    const { schoolChoice } = this.state;

    this.setState({ status: STATUS.SUBMITTING, errors: {} });
    return selectSuperAdminSchool(schoolChoice.schoolChoiceToken, school.id)
      .then(result => this.handleAuthResult(result))
      .catch((error) => {
        this.setState({
          errors: { saveError: error.message },
          status: STATUS.SUBMITTED,
          contextChoice: null,
          schoolChoice,
          ssoProfileCompletion: null,
        });
      });
  };

  handleSsoProfileSubmit = (details) => {
    const { completeSsoProfile } = this.props;
    const { ssoProfileCompletion } = this.state;
    return completeSsoProfile(
      ssoProfileCompletion.profileCompletionToken,
      details,
    ).then(result => this.handleAuthResult(result));
  };

  handleGoogleFailure = () => {
    if (!this.googleAuthRequested || this.isCancelled) {
      return Promise.resolve();
    }

    this.setState({
      errors: { saveError: GOOGLE_LOGIN_FAILURE_MESSAGE },
      status: STATUS.SUBMITTED,
    });

    return Promise.resolve();
  };

  validate() {
    const { credentials } = this.state;
    const errors = validateValues(credentials, validationConfig);
    this.setState({ errors });
    return hasNoErrors(errors);
  }

  render() {
    const {
      credentials, touched, errors, status, contextChoice, schoolChoice, ssoProfileCompletion,
    } = this.state;
    const disabled = status === STATUS.SUBMITTING;

    if (contextChoice) {
      return (
        <ContextChoice
          contexts={contextChoice.availableContexts || []}
          onSelect={this.handleContextSelect}
          disabled={disabled}
          error={errors.saveError}
        />
      );
    }

    if (schoolChoice) {
      return (
        <SuperAdminSchoolChoice
          schools={schoolChoice.availableSchools || []}
          onSelect={this.handleSchoolSelect}
          disabled={disabled}
          error={errors.saveError}
        />
      );
    }

    if (ssoProfileCompletion) {
      return (
        <SsoProfileCompletion
          email={ssoProfileCompletion.email}
          firstName={ssoProfileCompletion.firstName}
          lastName={ssoProfileCompletion.lastName}
          onSubmit={this.handleSsoProfileSubmit}
        />
      );
    }

    return (
      <Translation>{tr => (<div>
        {Object.keys(errors).length > 0 && status === STATUS.SUBMITTED && (
          <div role="alert" className="alert alert-danger">
            <p><LocalizedMessage value="Błędy na stronie:" /></p>
            <ul>
              {Object.keys(errors).map(key => <li key={key}><LocalizedMessage isError value={errors[key]} /></li>)}
            </ul>
          </div>
        )}
        <LoginForm
          username={credentials.username}
          password={credentials.password}
          touched={touched}
          errors={errors}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
          onSubmit={this.handleSubmit}
          onCancel={handleCancel}
          status={status}
          disabled={disabled}
          ssoButton={(
            <div className="position-relative" onClickCapture={this.handleGoogleAttempt}>
              <div
                ref={this.googleButtonRef}
                className="manage-google-sso-slot"
                aria-hidden={disabled ? "true" : "false"}
              />
              {disabled && (
                <button
                  type="button"
                  className="btn manage-button manage-teal-button btn-block position-absolute"
                  disabled
                  style={{ inset: 0, opacity: 0 }}
                  aria-label={tr("auth:signInSso")}
                >{tr("auth:signInSso")}</button>
              )}
            </div>
          )}
        />
      </div>)}</Translation>
    );
  }
}

Login.propTypes = {
  signIn: PropTypes.func.isRequired,
  signInWithGoogle: PropTypes.func.isRequired,
  selectLoginContext: PropTypes.func.isRequired,
  selectSuperAdminSchool: PropTypes.func.isRequired,
  completeSsoProfile: PropTypes.func.isRequired,
};

const mapStateToProps = () => ({});

const mapDispatchToProps = {
  signIn: userActions.signIn,
  signInWithGoogle: userActions.signInWithGoogle,
  selectLoginContext: userActions.selectLoginContext,
  selectSuperAdminSchool: userActions.selectSuperAdminSchool,
  completeSsoProfile: userActions.completeSsoProfile,
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
